import { Box, Button, Skeleton, Stack } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import useSwr from "swr"
import { NetworkApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { OrderForm } from "../../components/order"
import { usePermission } from "../../components/permission"
import { ListState } from "../../components/ui"

/** «Харилцагчаар захиалах»: load, error (retry, back to the list) and not-found states before the order form. */
export function SupplierCustomerOrder() {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(["supplierSales", "common"])

  // One permission model: the entry button needs ORD_NET_NEW_ORDER, the form itself ORD_SAVE or ORD_SEND (BE order/create.ts).
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")
  const canOrder = ORD_SAVE.hasAccess || ORD_SEND.hasAccess

  const { data, error, isLoading, mutate } = useSwr(
    canOrder ? `/ord/network/get.${params.id}` : null,
    async () => await NetworkApi.get(params.id as string),
  )

  const frame = (children: React.ReactNode) => (
    <PageLayout
      title={t("supplierSales:customer.orderDetailTitle")}
      breadcrumb={[
        { to: "/", label: t("common:breadcrumb.home") },
        { to: "/supplier-customer", label: t("supplierSales:customer.title") },
        { label: t("supplierSales:customer.orderDetailTitle") },
      ]}>
      <Box>{children}</Box>
    </PageLayout>
  )

  if (!canOrder) return frame(ORD_SAVE.accessDenied())

  if (isLoading && !data) {
    return frame(
      <Stack gap="md">
        <Skeleton height={160} radius="lg" />
        <Skeleton height={320} radius="lg" />
      </Stack>,
    )
  }
  const status = (error as { statusCode?: number } | undefined)?.statusCode
  if (error || !data) {
    return frame(
      <Stack gap="md" align="flex-start">
        {status === 404 || !error ? (
          <ListState
            state="empty"
            title={t("supplierSales:customer.notFoundTitle")}
            description={t("supplierSales:customer.notFoundText")}
          />
        ) : (
          <ListState state="error" onRetry={() => mutate()} errorCode={status} />
        )}
        <Button variant="default" onClick={() => navigate("/supplier-customer")}>{t("supplierSales:customer.backToList")}</Button>
      </Stack>,
    )
  }

  return <OrderForm mode="create" side="supplier" preselectedPartner={data} backPath="/supplier-customer" />
}
